import { requireNonNullable } from '@disy/lib/custom-error';
import type { MenuContentElement } from '@disy/ui/menu';
import { MENU_ITEM_SEPARATOR } from '@disy/ui/menu';

import { errorAlert } from 'cadenza/alert';
import type { ObjecttypeMemberDto } from 'cadenza/api-client/dto/objecttype-member-dto';
import { getObjecttypeMember } from 'cadenza/api-client/objecttypes-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { isModified } from 'cadenza/attribute-util';
import { showCreateTimeHierarchyFromAttributeDialog } from 'cadenza/objecttype/objecttype-details/attributes-overview/attributes-overview-util';
import { getStore } from 'cadenza/store/cadenza-store';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { createEditAttributeAction } from 'cadenza/workbook/attribute-modification-dialog/edit-attribute';
import { confirmSavePublishedWorkbookBeforeAction } from 'cadenza/workbook/confirm-save-and-execute-dialog/confirm-save-published-workbook';
import { executeSaveWorkbook } from 'cadenza/workbook/header/save-workbook-actions/save-workbook-actions';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import { isLobAttributeWithoutRelation } from 'cadenza/workbook/workbook-attribute-util';
import { executeCommand, fetchPartialState } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getAttributeById,
  getObjecttypeById,
  hasObjecttypeDatasourceCapabilities,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { analysisContextAccepts } from 'cadenza/workbook/workbook-panel/analysis-context/analysis-context-attribute-bundle-definition';
import { createEnrichAttributeActions } from 'cadenza/workbook/workbook-panel/databrowser/create-enrichment-context-menu-utilities';
import {
  addAttributeToAnalysisContextAction,
  removeLocalAttribute,
} from 'cadenza/workbook/workbook-panel/databrowser/data-browser-attribute-context-actions';
import { userCanCreateHierarchyOnWorkbookAndObjecttypeRepository } from 'cadenza/workbook/workbook-panel/databrowser/data-browser-controller-utils';
import { getEnrichmentAttributeActions } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-details-context-menu-utilities';
import { getWorkbook } from 'cadenza/workbook/workbook-utils';

import i18n from './data-browser-controller.properties';
import './data-browser-panel.css';

/**
 * Controller for attribute modifications on data browser.
 *
 * @class
 * @implements {AttributeModificationController}
 */
export class DataBrowserController {
  /**
   * Creates menu items reflecting options for the given attribute.
   *
   * @param attributeId - the attribute to create menu items for
   * @return the menu items created
   */
  async getMenuItems(attributeId: AttributeId) {
    const items: MenuContentElement[] = [
      MENU_ITEM_SEPARATOR,
      ...this.#getAddAttributeAction(attributeId),
      ...this.#getAddToAnalysisContextAction(attributeId),
      ...(await this.#getCreateTimeHierarchyAction(attributeId)),
      ...this.#getCreateLocalAttributeWithModifiedOlapAttributeDefinitionAction(attributeId),
      await this.#getDataEnrichmentSubMenu(attributeId),
    ];
    if (items.length > 2) {
      items.push(MENU_ITEM_SEPARATOR);
    }
    items.push(
      createEditAttributeAction(attributeId),
      ...this.#getResetAttributeAction(attributeId),
      ...this.#getRemoveLocalAttributeAction(attributeId),
    );
    const enrichmentSubmenu = await getEnrichmentAttributeActions(getAttributeById(getStore().getState(), attributeId));
    if (enrichmentSubmenu) {
      items.push(enrichmentSubmenu);
    }
    return items;
  }

  #getAddAttributeAction(attributeId: AttributeId) {
    const activeView = getWorkbook().activeView;
    const attribute = getAttributeById(getStore().getState(), attributeId);
    if (activeView?.isAccepted(attribute) && !isLobAttributeWithoutRelation(attribute)) {
      return [
        {
          label: i18n('attribute.action.add'),
          execute: () => activeView.addAttribute(attributeId),
          disabled: activeView.getDataViewThatAcceptsAttribute(attributeId) === undefined,
        },
      ];
    }
    return [];
  }

  #getAddToAnalysisContextAction(attributeId: AttributeId) {
    if (
      !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context') ||
      !isWorkbookFeatureAvailable(getStore().getState(), 'workbook-condition-management')
    ) {
      return [];
    }

    const attribute = getAttributeById(getStore().getState(), attributeId);
    if (!analysisContextAccepts(attribute)) {
      return [];
    }

    return [
      {
        label: i18n('attribute.action.addToAnalysisContext'),
        execute: () => addAttributeToAnalysisContextAction(attributeId),
      },
    ];
  }

  async #getCreateTimeHierarchyAction(attributeId: AttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    const objecttype = getObjecttypeById(getStore().getState(), attribute.objecttypeId);
    const checkResult = await userCanCreateHierarchyOnWorkbookAndObjecttypeRepository(objecttype);
    if (
      checkResult !== 'disallowed' &&
      attribute.type === 'DATE' &&
      attribute.category === 'DIMENSION' &&
      hasObjecttypeDatasourceCapabilities(getStore().getState(), attribute.objecttypeId, ['DATE_HIERARCHIES'])
    ) {
      const objecttypeMember = await getObjecttypeMember(attribute.objecttypeId, attribute.name);
      return [
        {
          label: i18n('attribute.action.createTimeHierarchy'),
          execute: async () => {
            if (checkResult === 'allowedWithNewVersion') {
              const confirmation = await confirmSavePublishedWorkbookBeforeAction();
              if (confirmation) {
                await executeSaveWorkbook();
                await this.#showCreateDateHierarchyDialog(objecttypeMember, attribute);
              }
            } else {
              await this.#showCreateDateHierarchyDialog(objecttypeMember, attribute);
            }
          },
        },
      ];
    }
    return [];
  }

  async #showCreateDateHierarchyDialog(objecttypeMember: ObjecttypeMemberDto, attribute: WorkbookAttributeDto) {
    const confirmed = await showCreateTimeHierarchyFromAttributeDialog(
      requireNonNullable(objecttypeMember.attribute),
      i18n('attribute.action.createTimeHierarchy.name.suffix'),
    );
    if (confirmed) {
      getStore().dispatch(fetchPartialState({ objecttypeIds: [attribute.objecttypeId] }));
    }
  }

  async #getDataEnrichmentSubMenu(enrichedAttributeId: AttributeId) {
    if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
      const attribute = getAttributeById(getStore().getState(), enrichedAttributeId);
      if (attribute.category !== 'HIERARCHY') {
        return createEnrichAttributeActions(attribute);
      }
    }
  }

  #getRemoveLocalAttributeAction(attributeId: AttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    if (attribute.localAttributeType) {
      return [
        MENU_ITEM_SEPARATOR,
        {
          label: i18n('deleteLocalAttribute'),
          execute: () => removeLocalAttribute({ attributeId: attribute.id as AttributeId }),
        },
      ];
    }
    return [];
  }

  #getCreateLocalAttributeWithModifiedOlapAttributeDefinitionAction(attributeId: AttributeId) {
    const attribute = getAttributeById(getStore().getState(), attributeId);
    if (
      isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management') &&
      !attribute.hierarchyType &&
      !attribute.localAttributeType &&
      attribute.type !== 'GEOMETRY'
    ) {
      const label = i18n(
        `attribute.action.createLocalAttributeWithModifiedOlapAttributeDefinition.${attribute.category}`,
      );
      return [
        {
          label,
          execute: () => {
            import(
              './create-local-attribute-with-modified-olap-attribute-definition-dialog/create-local-attribute-with-modified-olap-attribute-definition-dialog'
            )
              .then(({ CreateLocalAttributeWithModifiedOlapAttributeDefinitionDialog }) => {
                return new CreateLocalAttributeWithModifiedOlapAttributeDefinitionDialog(attribute).connectAndShow();
              })
              .catch(() =>
                errorAlert(
                  i18n(
                    `error.dialog.createLocalAttributeWithModifiedOlapAttributeDefinition.title.${attribute.category}`,
                  ),
                  i18n('error.dialog.message'),
                ),
              );
          },
        },
      ];
    }
    return [];
  }

  #getResetAttributeAction(attributeId: AttributeId) {
    if (isModified(getAttributeById(getStore().getState(), attributeId))) {
      return [
        {
          label: i18n('attribute.action.reset'),
          execute: () =>
            getStore().dispatch(executeCommand(Command.RESET_WORKBOOK_ATTRIBUTE_MODIFICATION({ attributeId }))),
        },
      ];
    }
    return [];
  }
}
